import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import { PRESET_CURRENT_FY, PRESET_CUSTOM, PRESET_LAST_12_MONTHS, PRESET_LIFETIME, PRESET_YTD } from '../hooks/useSummaryFilters';

import Button from 'CommonComponents/Button';

function PresetChip({ active, disabled, onClick, children }) {
    return (
        <button
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={onClick}
            className={classNames('tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-border-2 tw-transition-colors', {
                'tw-bg-blue tw-text-white tw-border-blue': active && !disabled,
                'tw-bg-white tw-text-blue tw-border-blue hover:tw-bg-blue-10': !active && !disabled,
                'tw-bg-white tw-text-charcoal-25 tw-border-grey tw-cursor-not-allowed': disabled,
            })}
        >
            {children}
        </button>
    );
}

const formatDate = (date) => (date ? moment(date).format('DD MMM YYYY') : null);

/**
 * Preset chips, the dates the selected range covers, and — for Custom — From
 * and To fields that apply together, so a half-typed range never reloads.
 *
 * `coveredRange` is the range the data was last read for: Lifetime's dates
 * are resolved by the server from the selected projects.
 */
export default function DateRangeControl({ dateRange, coveredRange, presets, onApplyPreset, onApplyLifetime, onApplyCustom }) {
    const presetId = dateRange.presetId;
    const [customOpen, setCustomOpen] = useState(presetId === PRESET_CUSTOM);
    const [draft, setDraft] = useState({ from: dateRange.from || '', to: dateRange.to || '' });

    useEffect(() => {
        if (presetId !== PRESET_CUSTOM) {
            setCustomOpen(false);
        }
    }, [presetId]);

    // The value is read before the updater runs: React 16 reuses the event.
    const setDraftField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

    const openCustom = () => {
        setDraft({ from: dateRange.from || coveredRange?.from || '', to: dateRange.to || coveredRange?.to || '' });
        setCustomOpen(true);
    };

    const inverted = !!draft.from && !!draft.to && draft.from > draft.to;
    const canApply = !!draft.from && !!draft.to && !inverted;

    const from = formatDate(coveredRange?.from);
    const to = formatDate(coveredRange?.to);

    return (
        <div>
            <div className="tw-text-sm tw-text-blue-75 tw-font-medium tw-mb-2">{$t('Date range')}</div>
            <div className="tw-flex tw-flex-wrap tw-gap-2">
                <PresetChip active={presetId === PRESET_LIFETIME && !customOpen} onClick={onApplyLifetime}>
                    {$t('Lifetime')}
                </PresetChip>
                <PresetChip active={presetId === PRESET_YTD && !customOpen} disabled={!presets?.ytd} onClick={() => onApplyPreset(PRESET_YTD, presets.ytd)}>
                    {$t('Year to date')}
                </PresetChip>
                <PresetChip
                    active={presetId === PRESET_CURRENT_FY && !customOpen}
                    disabled={!presets?.current_fy}
                    onClick={() => onApplyPreset(PRESET_CURRENT_FY, presets.current_fy)}
                >
                    {presets?.current_fy?.label ? $t('FY {{label}}', { label: presets.current_fy.label }) : $t('Current FY')}
                </PresetChip>
                <PresetChip
                    active={presetId === PRESET_LAST_12_MONTHS && !customOpen}
                    disabled={!presets?.last_12_months}
                    onClick={() => onApplyPreset(PRESET_LAST_12_MONTHS, presets.last_12_months)}
                >
                    {$t('Last 12 months')}
                </PresetChip>
                <PresetChip active={customOpen} onClick={openCustom}>
                    {$t('Custom')}
                </PresetChip>
            </div>

            {from && to && <div className="tw-text-xs tw-text-charcoal-50 tw-mt-1.5">{$t('{{from}} to {{to}}', { from, to })}</div>}

            {customOpen && (
                <div className="tw-mt-3">
                    <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
                        <div>
                            <label htmlFor="summary-range-from" className="tw-block tw-text-xs tw-text-charcoal-50 tw-mb-1">
                                {$t('From')}
                            </label>
                            <input
                                id="summary-range-from"
                                type="date"
                                value={draft.from}
                                onChange={(e) => setDraftField('from', e.target.value)}
                                className="tw-border-2 tw-border-grey tw-rounded-lg tw-text-sm tw-p-1.5"
                            />
                        </div>
                        <div>
                            <label htmlFor="summary-range-to" className="tw-block tw-text-xs tw-text-charcoal-50 tw-mb-1">
                                {$t('To')}
                            </label>
                            <input
                                id="summary-range-to"
                                type="date"
                                value={draft.to}
                                onChange={(e) => setDraftField('to', e.target.value)}
                                className="tw-border-2 tw-border-grey tw-rounded-lg tw-text-sm tw-p-1.5"
                            />
                        </div>
                        <Button variant="primary" disabled={!canApply} onClick={() => onApplyCustom(draft.from, draft.to)}>
                            {$t('Apply')}
                        </Button>
                    </div>
                    {inverted && <div className="tw-text-xs tw-text-red tw-mt-1.5">{$t('The From date must be on or before the To date.')}</div>}
                </div>
            )}
        </div>
    );
}
